import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, MapPin, Award, Star, Loader2, Navigation } from 'lucide-react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { useLocations } from '../happyspace-restaurant-sdk';
import { BRAND_NAME, BRAND_DOMAIN } from '../config/brand';

export default function HomePage() {
  const { locations, nearestLocation, loading, error } = useLocations();
  const displayLocations = locations.length > 0 ? locations : [];
  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Restaurant",
    "name": BRAND_NAME,
    "image": "https://storage.googleapis.com/happyspace-generated-images/generated/2026-02-05/642d69677d16.png",
    "description": "Authentic Mediterranean & Afghan grilled-to-order kebabs. 100% Halal certified, fresh ingredients, never frozen.",
    "servesCuisine": "Mediterranean, Afghan",
    "priceRange": "$",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "7750 N MacArthur Blvd",
      "addressLocality": "Irving",
      "addressRegion": "TX",
      "postalCode": "75063",
      "addressCountry": "US"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 32.9126,
      "longitude": -96.9584
    },
    "url": `https://${BRAND_DOMAIN}`,
    "telephone": "+1-972-555-0123",
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "11:00",
        "closes": "22:00"
      }
    ]
  };

  return (
    <div className="overflow-hidden">
      <Helmet>
        <title>{BRAND_NAME} | #1 Halal Kebabs in Dallas-Fort Worth</title>
        <meta name="description" content="Authentic Mediterranean & Afghan grilled-to-order kebabs in Dallas, Irving, and Frisco. 100% Halal certified, fresh ingredients, family recipes. Open late night!" />
        <meta name="keywords" content="halal kebabs dallas, mediterranean food irving, afghan cuisine frisco, grilled kebabs, late night food dallas" />
        <meta property="og:title" content={`${BRAND_NAME} | #1 Halal Kebabs in Dallas`} />
        <meta property="og:description" content="Authentic Mediterranean & Afghan grilled-to-order kebabs. 100% Halal certified." />
        <meta property="og:image" content="https://storage.googleapis.com/happyspace-generated-images/generated/2026-02-05/642d69677d16.png" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[85vh] flex items-center" data-hs-id="hero-section">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://storage.googleapis.com/happyspace-generated-images/generated/2026-02-05/642d69677d16.png"
            alt="Delicious Grilled Kebabs"
            className="w-full h-full object-cover brightness-[0.4]"
            data-hs-id="hero-bg-image"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-2xl">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 bg-[#D97706]/20 backdrop-blur-md border border-[#D97706]/30 px-4 py-2 rounded-full mb-6">
                <Award className="w-5 h-5 text-[#F59E0B]" />
                <span className="text-[#F59E0B] font-semibold text-sm uppercase tracking-wider" data-hs-id="hero-badge">#1 Halal Kebabs in Dallas</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight" data-hs-id="hero-title">
                Grilled to Order, <br />
                <span className="text-[#F59E0B]">Perfected by Tradition</span>
              </h1>
              <p className="text-xl text-gray-200 mb-8 max-w-lg" data-hs-id="hero-subtitle">
                Authentic Mediterranean & Afghan family recipes. 100% Halal certified, fresh ingredients, never frozen.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link 
                  to="/menu" 
                  className="bg-[#D97706] text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-[#B45309] hover:scale-105 active:scale-100 transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-amber-900/20"
                >
                  Order Online <ArrowRight className="w-5 h-5" />
                </Link>
                <a 
                  href="#locations" 
                  className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-4 rounded-full font-bold text-lg hover:bg-white/20 hover:scale-105 active:scale-100 transition-all duration-200 flex items-center justify-center gap-2"
                >
                  Find Location
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            <div className="text-center" data-hs-id="feature-1">
              <div className="w-16 h-16 bg-[#FFFBEB] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Award className="w-8 h-8 text-[#D97706]" />
              </div>
              <h3 className="text-xl font-bold text-[#451A03] mb-3">100% Halal Certified</h3>
              <p className="text-gray-600">We take pride in our strict halal standards, ensuring every meal is prepared with integrity.</p>
            </div>
            <div className="text-center" data-hs-id="feature-2">
              <div className="w-16 h-16 bg-[#FFFBEB] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Clock className="w-8 h-8 text-[#D97706]" />
              </div>
              <h3 className="text-xl font-bold text-[#451A03] mb-3">Open Late Night</h3>
              <p className="text-gray-600">Cravings don't have a schedule. Our flagship location stays open until 2 AM for your late-night fixes.</p>
            </div>
            <div className="text-center" data-hs-id="feature-3">
              <div className="w-16 h-16 bg-[#FFFBEB] rounded-2xl flex items-center justify-center mx-auto mb-6">
                <Star className="w-8 h-8 text-[#D97706]" />
              </div>
              <h3 className="text-xl font-bold text-[#451A03] mb-3">Family Recipes</h3>
              <p className="text-gray-600">Generations of Afghan and Mediterranean culinary heritage in every bite of our grilled-to-order kebabs.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Location Selector - from backend, nearest first */}
      <section id="locations" className="py-24 bg-[#FFFBEB]" data-hs-id="locations-section">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#451A03] mb-4">Visit Our Locations</h2>
            <p className="text-gray-600">
              {displayLocations.length > 0
                ? `${displayLocations.length} convenient spot${displayLocations.length === 1 ? '' : 's'} near you.`
                : 'Find us at our locations.'}
            </p>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-4">
              <Loader2 className="w-10 h-10 animate-spin text-[#D97706]" />
              <p className="text-gray-500">Loading locations...</p>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-gray-500">Unable to load locations. <Link to="/menu" className="text-[#D97706] font-medium hover:underline">Order from menu</Link></p>
            </div>
          ) : displayLocations.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border border-orange-100">
              <MapPin className="w-12 h-12 text-[#D97706] mx-auto mb-4" />
              <p className="text-gray-500">No locations yet. Check back soon.</p>
              <Link to="/menu" className="inline-block mt-4 text-[#D97706] font-bold hover:underline">Order Online</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {displayLocations.map((loc, i) => {
                const isNearest = nearestLocation && (loc._id === nearestLocation._id || loc.name === nearestLocation.name);
                return (
                  <div
                    key={loc._id || i}
                    className={`bg-white p-8 rounded-3xl shadow-sm border-2 transition-colors group ${
                      isNearest ? 'border-[#D97706] ring-2 ring-[#D97706]/20' : 'border-orange-100 hover:border-[#D97706]'
                    }`}
                  >
                    {isNearest && (
                      <span className="inline-block px-3 py-1 bg-[#D97706] text-white text-xs font-bold rounded-full mb-4">Nearest</span>
                    )}
                    <MapPin className="w-8 h-8 text-[#D97706] mb-4" />
                    <h4 className="text-xl font-bold text-[#451A03] mb-2">{loc.name}</h4>
                    {loc.address && <p className="text-gray-500 mb-2">{loc.address}</p>}
                    {loc.phone && <p className="text-sm text-gray-500 mb-4">{loc.phone}</p>}
                    {loc.distance != null && <p className="text-xs text-[#D97706] font-medium mb-4">{loc.distance.toFixed(1)} mi away</p>}
                    <div className="flex flex-wrap gap-2">
                      <Link to="/menu" className="text-[#D97706] font-bold flex items-center gap-2 group-hover:gap-3 transition-all">
                        Order <ArrowRight className="w-4 h-4" />
                      </Link>
                      {loc.address && (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(loc.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-500 hover:text-[#D97706] flex items-center gap-1 text-sm"
                        >
                          <Navigation className="w-4 h-4" /> Directions
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-[#451A03] rounded-[3rem] p-12 md:p-20 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-[#D97706]/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="relative z-10 max-w-2xl">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Hungry for the best <br /> kebabs in town?</h2>
              <p className="text-orange-100/80 text-xl mb-10">Experience the authentic taste of grilled-to-order perfection. Order online for pickup or delivery.</p>
              <Link 
                to="/menu" 
                className="inline-block bg-[#D97706] text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#451A03] transition-all"
              >
                Start Your Order
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}